import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_NAMES = {
  '/': 'Dashboard',
  '/dashboard': 'Dashboard',
  '/charts': 'Charts',
  '/insights': 'Insights',
  '/leads': 'Manage Leads',
  '/transferred-leads': 'Transferred Leads',
  '/duplicate-leads': 'Duplicate Leads',
  '/data-management': 'Data Management',
  '/dispatch': 'Dispatch',
  '/stock': 'Stock Checker',
  '/tenders': 'Tenders',
  '/comparison': 'Comparison',
  '/forecast': 'Forecast',
  '/compare-forecasts': 'Compare Forecasts',
  '/admin': 'Admin Panel',
  '/login': 'Login',
  '/dealer-scorecard': 'Dealer Scorecard',
  '/sales-assistance': 'Sales Assistance',
  '/customers': 'Customer Intelligence',
  '/social': 'Social Media Dashboard',
  '/social/upload': 'Social Media Upload',
  '/social/templates': 'Social Media Templates',
};

function getPageName(pathname) {
  if (PAGE_NAMES[pathname]) return PAGE_NAMES[pathname];
  if (pathname.startsWith('/profile/')) return 'Entity Profile';
  if (pathname.startsWith('/customers/')) return 'Customer Profile';
  if (pathname.startsWith('/social/content/')) return 'Social Content Detail';
  return pathname;
}

// Throttle tracking calls to avoid spamming (max 1 call per action per 500ms)
const _lastTrack = {};
function _shouldTrack(key) {
  const now = Date.now();
  if (_lastTrack[key] && now - _lastTrack[key] < 500) return false;
  _lastTrack[key] = now;
  return true;
}

export function trackAction(action, resourceType = 'app', resourceId = null, details = null) {
  const key = `${action}_${resourceType}_${resourceId || ''}`;
  if (!_shouldTrack(key)) return;
  axios.post(`${API}/admin/track`, {
    action,
    resource_type: resourceType,
    resource_id: resourceId,
    details,
  }, { withCredentials: true }).catch(() => {});
}

export function useActivityTracker() {
  const location = useLocation();
  const { user, loading } = useAuth();
  const prevPath = useRef(null);

  const track = useCallback((action, resourceType, resourceId, details) => {
    trackAction(action, resourceType, resourceId, details);
  }, []);

  // Track page views
  useEffect(() => {
    if (loading) return;
    if (!user) { prevPath.current = null; return; }
    if (location.pathname === prevPath.current) return;
    if (location.pathname === '/login') return;
    prevPath.current = location.pathname;

    trackAction('page_view', 'navigation', null, {
      page: getPageName(location.pathname),
      path: location.pathname,
    });
  }, [location.pathname, loading, user]);

  return { track };
}
